/**
 * Custom Auth Storage para Supabase
 * 
 * Implementa um storage híbrido que usa cookies para compartilhar
 * sessão entre subdomínios, com fallback para localStorage
 * 
 * ⚠️ LIMITAÇÕES DE SEGURANÇA:
 * - Cookies não podem usar HttpOnly porque o Supabase JS SDK precisa ler via JavaScript
 * - Isso torna a aplicação vulnerável a XSS (Cross-Site Scripting)
 * - IMPORTANTE: Implementar CSP (Content Security Policy) rigoroso
 * - IMPORTANTE: Sanitizar TODOS os inputs de usuário
 * - IMPORTANTE: Usar DOMPurify para conteúdo HTML dinâmico
 * 
 * Para segurança máxima, seria necessário:
 * - Backend proxy para gerenciar tokens com HttpOnly cookies
 * - Supabase Server-Side Auth
 */

const COOKIE_DOMAIN = '.imobpanda.com';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 dias (reduzido de 30 para minimizar janela de ataque)
const DEBUG_MODE = import.meta.env.DEV; // Apenas em desenvolvimento

/**
 * Detecta se está em produção (imobpanda.com) ou desenvolvimento (localhost)
 */
const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !window.location.hostname.includes('localhost');
};

/**
 * Obtém o domínio correto para os cookies
 */
const getCookieDomain = (): string => {
  return isProduction() ? COOKIE_DOMAIN : '';
};

/**
 * Parse de cookies do navegador
 * Implementa cache para melhorar performance
 */
let cookieCache: { cookies: Record<string, string>; timestamp: number } | null = null;
const CACHE_TTL = 1000; // 1 segundo

const parseCookies = (): Record<string, string> => {
  if (typeof document === 'undefined') return {};
  
  // Cache para evitar parsing repetido (performance)
  const now = Date.now();
  if (cookieCache && (now - cookieCache.timestamp) < CACHE_TTL) {
    return cookieCache.cookies;
  }
  
  const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
    const [key, value] = cookie.split('=');
    if (key && value) {
      try {
        acc[key] = decodeURIComponent(value);
      } catch (error) {
        // Ignorar cookies malformados
        if (DEBUG_MODE) {
          console.warn(`[CustomStorage] Failed to decode cookie: ${key}`);
        }
      }
    }
    return acc;
  }, {} as Record<string, string>);
  
  cookieCache = { cookies, timestamp: now };
  return cookies;
};

/**
 * Define um cookie com configurações de segurança
 */
const setCookie = (key: string, value: string): void => {
  if (typeof document === 'undefined') return;
  
  const domain = getCookieDomain();
  const domainAttr = domain ? `domain=${domain};` : '';
  const secureAttr = isProduction() ? 'Secure;' : '';
  
  // ⚠️ Não podemos usar HttpOnly porque o Supabase JS precisa ler via JavaScript
  // SameSite=Strict seria mais seguro, mas Lax é necessário para OAuth redirects
  document.cookie = `${key}=${encodeURIComponent(value)}; ${domainAttr} path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; ${secureAttr}`;
  
  // Invalidar cache
  cookieCache = null;
  
  if (DEBUG_MODE) {
    console.log(`[CustomStorage] Cookie set: ${key} (domain: ${domain || 'localhost'})`);
  }
};

/**
 * Remove um cookie
 */
const removeCookie = (key: string): void => {
  if (typeof document === 'undefined') return;
  
  const domain = getCookieDomain();
  const domainAttr = domain ? `domain=${domain};` : '';
  
  document.cookie = `${key}=; ${domainAttr} path=/; max-age=0`;
  
  // Invalidar cache
  cookieCache = null;
  
  if (DEBUG_MODE) {
    console.log(`[CustomStorage] Cookie removed: ${key}`);
  }
};

/**
 * Custom Storage que usa cookies com fallback para localStorage
 */
export const customAuthStorage = {
  /**
   * Obtém um item do storage
   * Tenta cookies primeiro (cross-subdomain), depois localStorage (local)
   * 
   * Performance: O(1) com cache de cookies
   */
  getItem: (key: string): string | null => {
    try {
      // Tentar cookie primeiro (funciona entre subdomínios)
      const cookies = parseCookies();
      if (cookies[key]) {
        if (DEBUG_MODE) {
          console.log(`[CustomStorage] Retrieved from cookie: ${key}`);
        }
        return cookies[key];
      }
      
      // Fallback para localStorage (mais rápido, mas não compartilhado)
      if (typeof window !== 'undefined' && window.localStorage) {
        const value = localStorage.getItem(key);
        if (value) {
          if (DEBUG_MODE) {
            console.log(`[CustomStorage] Retrieved from localStorage: ${key}`);
          }
          return value;
        }
      }
      
      if (DEBUG_MODE) {
        console.log(`[CustomStorage] Not found: ${key}`);
      }
      return null;
    } catch (error) {
      // Não logar erro em produção (pode expor informações)
      if (DEBUG_MODE) {
        console.error(`[CustomStorage] Error getting item ${key}:`, error);
      }
      return null;
    }
  },

  /**
   * Salva um item no storage
   * Salva em ambos: cookies (cross-subdomain) E localStorage (backup + performance)
   * 
   * Performance: O(1)
   * Segurança: Dados duplicados para redundância
   */
  setItem: (key: string, value: string): void => {
    try {
      // Validação básica de tamanho (cookies têm limite de ~4KB)
      if (value.length > 4000) {
        console.warn(`[CustomStorage] Value too large for cookie (${value.length} bytes), using localStorage only`);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(key, value);
        }
        return;
      }
      
      // Salvar em cookie (compartilhado entre subdomínios)
      setCookie(key, value);
      
      // Salvar também em localStorage (backup local + leitura mais rápida)
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
        if (DEBUG_MODE) {
          console.log(`[CustomStorage] Saved to localStorage: ${key}`);
        }
      }
    } catch (error) {
      // Não logar erro em produção
      if (DEBUG_MODE) {
        console.error(`[CustomStorage] Error setting item ${key}:`, error);
      }
      // Tentar salvar apenas em localStorage como fallback
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(key, value);
        }
      } catch (fallbackError) {
        // Silenciar erro em produção
      }
    }
  },

  /**
   * Remove um item do storage
   * Remove de ambos: cookies E localStorage
   * 
   * Performance: O(1)
   */
  removeItem: (key: string): void => {
    try {
      // Remover cookie
      removeCookie(key);
      
      // Remover de localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(key);
        if (DEBUG_MODE) {
          console.log(`[CustomStorage] Removed from localStorage: ${key}`);
        }
      }
    } catch (error) {
      // Não logar erro em produção
      if (DEBUG_MODE) {
        console.error(`[CustomStorage] Error removing item ${key}:`, error);
      }
    }
  },
};

/**
 * Utilitário para debug - lista todos os itens de auth no storage
 * ⚠️ APENAS EM DESENVOLVIMENTO - não expor em produção
 */
export const debugAuthStorage = (): void => {
  if (!DEBUG_MODE) {
    console.warn('[CustomStorage] Debug mode disabled in production');
    return;
  }
  
  console.group('[CustomStorage] Debug Info');
  console.log('Hostname:', window.location.hostname);
  console.log('Is Production:', isProduction());
  console.log('Cookie Domain:', getCookieDomain());
  
  console.log('\nCookies:');
  const cookies = parseCookies();
  Object.keys(cookies)
    .filter(k => k.includes('auth') || k.includes('supabase') || k.includes('imobpanda'))
    .forEach(key => {
      // Não mostrar valor completo por segurança
      console.log(`  ${key}:`, cookies[key].substring(0, 30) + '... [REDACTED]');
    });
  
  console.log('\nLocalStorage:');
  if (typeof window !== 'undefined' && window.localStorage) {
    Object.keys(localStorage)
      .filter(k => k.includes('auth') || k.includes('supabase') || k.includes('imobpanda'))
      .forEach(key => {
        const value = localStorage.getItem(key);
        // Não mostrar valor completo por segurança
        console.log(`  ${key}:`, value?.substring(0, 30) + '... [REDACTED]');
      });
  }
  
  console.groupEnd();
};

// Expor debugAuthStorage globalmente APENAS em desenvolvimento
if (typeof window !== 'undefined' && DEBUG_MODE) {
  (window as any).debugAuthStorage = debugAuthStorage;
  console.log('[CustomStorage] Debug mode enabled. Use window.debugAuthStorage() to inspect storage.');
}
